import React, { useCallback, useEffect, useState } from 'react';

import { EmptyState, ErrorState, ListaSelecao, Modal, Pagination, Skeleton } from '../../../ui';

import {
  fetchSgaCombustiveis,
  Combustivel,
} from '../../../api/sga-combustivel/sga-combustivelApi';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * Escolha do combustível (reescrito na at-59 sobre o `Modal` do kit).
 *
 * ## O que a reescrita mudou
 *
 *   1. **Visual do design system**, lista alcançável pelo teclado (era
 *      `<li onClick>`) e paginação em `<button>` (era `<div onClick>`).
 *   2. **Falha deixou de ser muda** — era toast + lista vazia.
 *   3. 🐛 **A paginação passou a funcionar.** Mesmo defeito do modal de cores:
 *      `fetchSgaCombustiveis(1, 20)` com página e limite fixos, enquanto o total
 *      de páginas era calculado com limite 10.
 *   4. 🐛 **O erro falava de "cooperativas"** — cópia do modal de cooperativas.
 *
 * ⚠️ **As props não mudaram** (`isOpen`/`onClose`/`onSelectCombustivel`).
 * 📌 Para motocicleta elétrica o combustível é definido pelo próprio catálogo
 * (`ELÉTRICO`, at-46) e este modal nem chega a ser aberto.
 */

interface ModalCombustiveisProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCombustivel: (combustivel: Combustivel) => void;
}

type Estado = 'carregando' | 'ok' | 'erro';

const POR_PAGINA = 10;

const ModalCombustiveis: React.FC<ModalCombustiveisProps> = ({
  isOpen,
  onClose,
  onSelectCombustivel,
}) => {
  const [combustiveis, setCombustiveis] = useState<Combustivel[]>([]);
  const [total, setTotal] = useState(0);
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    if (isOpen) setPagina(1);
  }, [isOpen]);

  const buscar = useCallback(async () => {
    setEstado('carregando');
    try {
      const resposta = await fetchSgaCombustiveis(pagina, POR_PAGINA);

      if (!resposta || !Array.isArray(resposta.items)) {
        setErro('A resposta do servidor não trouxe a lista de combustíveis.');
        setEstado('erro');
        return;
      }

      setCombustiveis(resposta.items);
      setTotal(resposta.total ?? 0);
      setEstado('ok');
    } catch (falha) {
      setErro(extrairMensagemErro(falha, 'Não foi possível carregar os combustíveis.'));
      setEstado('erro');
    }
  }, [pagina]);

  useEffect(() => {
    if (isOpen) void buscar();
  }, [isOpen, buscar]);

  return (
    <Modal
      aberto={isOpen}
      titulo="Selecionar Combustível"
      tamanho="sm"
      aoFechar={onClose}
      rodape={
        estado === 'ok' && combustiveis.length > 0 ? (
          <Pagination
            pagina={pagina}
            totalItens={total}
            porPagina={POR_PAGINA}
            aoTrocar={setPagina}
          />
        ) : undefined
      }
    >
      {estado === 'carregando' && <Skeleton linhas={4} altura="tw-h-11" />}

      {estado === 'erro' && <ErrorState mensagem={erro} aoTentarNovamente={() => void buscar()} />}

      {estado === 'ok' && (
        <ListaSelecao
          itens={combustiveis}
          chave={(c) => c.codigo_combustivel}
          rotulo={(c) => c.descricao}
          aoEscolher={(c) => {
            onSelectCombustivel(c);
            onClose();
          }}
          rotuloAcessivel="Combustíveis disponíveis"
          vazio={
            <EmptyState
              icone="sem-dados"
              titulo="Nenhum combustível encontrado"
              descricao="O espelho de combustíveis do SGA parece vazio. Fale com o TI."
            />
          }
        />
      )}
    </Modal>
  );
};

export default ModalCombustiveis;
